import {
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import {
  type CellContext,
  type ColumnDef,
  getCoreRowModel,
  type HeaderContext,
  type Row,
  useReactTable,
} from "@tanstack/react-table";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import {
  reportKeys,
  useBulkDeleteReportItems,
  useCopyReportItem,
  useReportItems,
  useReportVersionHeader,
  useUpdateReportVersionZeroTrade,
} from "@/hooks/useReportQueries";
import { handleApiError } from "@/hooks/useApiQueries";
import {
  Accordion,
  Button,
  ErrorDisplay,
  FormInput,
  HeadlessTable,
  Loader,
  Modal,
  NoData,
  PageTitle,
  ReportVersionHeader,
  StatusIndicator,
  Tooltip,
} from "@/shared/components";
import { useBreadcrumb, useToast } from "@/shared/hooks";
import { useAuth } from "@/auth";
import { formatReportingPeriod } from "@/utils/dateUtils";
import { formatDecimal } from "@/utils/numberUtils";
import type {
  UiReportItem,
  UiReportVersionDetails,
  UiValidationError,
} from "@/generated/ui-api";
import * as sdk from "@/generated/ui-api";
import IndicatorIcon from "@/assets/icons/IndicatorIcon";
import { useVersionColumns } from "@/hooks/useVersionColumns";

const ValidationErrorIcon = ({ errors }: { errors: UiValidationError[] }) => {
  if (!errors || errors.length === 0) {
    return null;
  }

  const errorMessages = errors.map(e => e.messageText).join("\n");

  return (
    <Tooltip content={errorMessages} placement="top" variant="error">
      <div className="cursor-pointer flex items-center">
        <IndicatorIcon variant="error" size={16} />
      </div>
    </Tooltip>
  );
};

const renderCellWithValidation = (info: CellContext<UiReportItem, unknown>) => {
  const value = info.getValue();
  const columnId = info.column.id;

  const numericColumns = [
    "netMass",
    "quantityInSupplementaryUnitOfMeasure",
    "invoicedAmount",
    "statisticalValue",
  ];

  let displayValue = String(value ?? "");
  if (
    numericColumns.includes(columnId) &&
    value !== null &&
    value !== undefined
  ) {
    const decimals =
      columnId === "netMass" ||
      columnId === "quantityInSupplementaryUnitOfMeasure"
        ? 3
        : 2;
    displayValue = formatDecimal(value as number, decimals);
  }

  const errors = info.row.original.validationErrors?.filter(
    err =>
      err.validationRuleImpact === "ERROR" &&
      err.validationRuleDataFields?.some(
        field => field.dataFieldName === info.column.id
      )
  );

  const meta = info.column.columnDef.meta as {
    wrapText?: boolean;
  };
  const wrapText = meta?.wrapText;

  const textClass = wrapText ? "break-words" : "truncate";

  return (
    <div className="flex items-center justify-center w-full min-w-0 h-full">
      <div className="w-4 shrink-0" aria-hidden="true" />

      <span
        className={`flex-1 min-w-0 text-center overflow-hidden ${textClass}`}
        title={displayValue}
      >
        {displayValue}
      </span>

      <div className="w-4 shrink-0 flex justify-center items-center">
        {errors && errors.length > 0 && <ValidationErrorIcon errors={errors} />}
      </div>
    </div>
  );
};

const PARTNER_COLUMN_MIN_WIDTH = 150;
const PARTNER_COLUMN_MAX_WIDTH = 300;

const NOMENCLATURE_COLUMN_MIN_WIDTH = 130;
const NOMENCLATURE_COLUMN_MAX_WIDTH = 200;

const estimateTextColumnWidth = (
  values: (string | null | undefined)[],
  {
    min,
    max,
    charWidth = 9,
    chrome = 48,
  }: { min: number; max: number; charWidth?: number; chrome?: number }
) => {
  const longest = values.reduce<number>(
    (acc, value) => Math.max(acc, value?.length ?? 0),
    0
  );
  return Math.min(max, Math.max(min, longest * charWidth + chrome));
};

const VALID_HEADER_FIELDS: string[] = [
  "vatPayerRegistrationInfo.vatPayerDocumentRegistrationNumber",
  "vatPayerRegistrationInfo.vatPayerDocumentRegistrationDate",
  "responsiblePersonInfo.preparationTime",
  "vatPayerInfo.vatPayerTitle",
  "vatPayerInfo.vatPayerIdentifierVAT",
  "vatPayerInfo.vatPayerAddress",
  "vatPayerInfo.vatPayerZipCode",
  "vatPayerInfo.vatPayerPhone",
  "vatPayerInfo.vatPayerEmail",
  "brokerInfo.brokerTitle",
  "brokerInfo.brokerIdentifierJAR",
  "brokerInfo.brokerAddress",
  "brokerInfo.brokerZipCode",
  "brokerInfo.brokerPhone",
  "brokerInfo.brokerEmail",
  "responsiblePersonInfo.responsiblePersonFirstLastName",
  "responsiblePersonInfo.responsiblePersonPhone",
  "responsiblePersonInfo.responsiblePersonEmail",
];

export default function ReportItemsPage() {
  const { t } = useTranslation();
  const { representedEntity } = useAuth();

  const { versionId } = useParams<{ versionId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();

  const passedData = location.state?.itemsData as
    | UiReportVersionDetails
    | undefined;

  const page = Number(searchParams.get("page")) || 1;
  const pageSize = 100;
  const versionIdNum = versionId ? Number(versionId) : 0;

  const [reportItemNumberFrom, setReportItemNumberFrom] = useState<string>(
    searchParams.get("reportItemNumberFrom") || ""
  );
  const [reportItemNumberTo, setReportItemNumberTo] = useState<string>(
    searchParams.get("reportItemNumberTo") || ""
  );
  const [combinedNomenclatureCode, setCombinedNomenclatureCode] =
    useState<string>(searchParams.get("combinedNomenclatureCode") || "");
  const [partnerIdentifier, setPartnerIdentifier] = useState<string>(
    searchParams.get("partnerIdentifier") || ""
  );
  const [errorFilter, setErrorFilter] = useState<string>(
    searchParams.get("error") || ""
  );
  const [warningFilter, setWarningFilter] = useState<string>(
    searchParams.get("warning") || ""
  );
  const [nonValidPartnerIndicatorFilter, setNonValidPartnerIndicatorFilter] =
    useState<string>(searchParams.get("nonValidPartnerIndicator") || "");
  const [hideDeletedFilter, setHideDeletedFilter] = useState<string>(
    searchParams.get("hideDeleted") ?? "true"
  );

  useEffect(() => {
    setReportItemNumberFrom(searchParams.get("reportItemNumberFrom") || "");
    setReportItemNumberTo(searchParams.get("reportItemNumberTo") || "");
    setCombinedNomenclatureCode(
      searchParams.get("combinedNomenclatureCode") || ""
    );
    setPartnerIdentifier(searchParams.get("partnerIdentifier") || "");
    setErrorFilter(searchParams.get("error") || "");
    setWarningFilter(searchParams.get("warning") || "");
    setNonValidPartnerIndicatorFilter(
      searchParams.get("nonValidPartnerIndicator") || ""
    );
    setHideDeletedFilter(searchParams.get("hideDeleted") ?? "true");
  }, [searchParams]);

  const hasActiveFilters =
    !!searchParams.get("reportItemNumberFrom") ||
    !!searchParams.get("reportItemNumberTo") ||
    !!searchParams.get("combinedNomenclatureCode") ||
    !!searchParams.get("partnerIdentifier") ||
    !!searchParams.get("error") ||
    !!searchParams.get("warning") ||
    !!searchParams.get("nonValidPartnerIndicator") ||
    (searchParams.has("hideDeleted") &&
      searchParams.get("hideDeleted") !== "true");

  const [isFiltersOpen, setIsFiltersOpen] = useState(hasActiveFilters);

  const isDefaultPage = page === 1 && pageSize === 100;
  const shouldIgnorePassedData = hasActiveFilters || !isDefaultPage;

  const [processingOperation, setProcessingOperation] = useState<
    "VALIDATE" | "SUBMIT" | null
  >(null);
  const handledOperationKeysRef = useRef<Set<string>>(new Set());
  const isValidatingReport = processingOperation === "VALIDATE";
  const isSubmittingReport = processingOperation === "SUBMIT";
  const [showCheckConfirm, setShowCheckConfirm] = useState(false);
  const [showSubmitConfirm, setShowSubmitConfirm] = useState(false);
  const [showBulkDeleteConfirm, setShowBulkDeleteConfirm] = useState(false);
  const [showCheckSuccess, setShowCheckSuccess] = useState(false);
  const [showSubmitSuccess, setShowSubmitSuccess] = useState(false);
  const [validationResult, setValidationResult] = useState<{
    hasErrors: boolean;
    hasWarnings: boolean;
  } | null>(null);

  const [rowSelection, setRowSelection] = useState<Record<string, boolean>>({});

  const { mutate: deleteItems, isPending: isDeleting } =
    useBulkDeleteReportItems(versionIdNum);
  const { mutate: copyItem, isPending: isCopying } =
    useCopyReportItem(versionIdNum);

  const {
    data: itemsDataFromHook,
    isLoading: loading,
    error,
    isError,
  } = useReportItems(versionIdNum, page, pageSize, {
    reportItemNumberFrom: searchParams.get("reportItemNumberFrom") || undefined,
    reportItemNumberTo: searchParams.get("reportItemNumberTo") || undefined,
    combinedNomenclatureCode:
      searchParams.get("combinedNomenclatureCode") || undefined,
    partnerIdentifier: searchParams.get("partnerIdentifier") || undefined,
    error: searchParams.get("error") || undefined,
    warning: searchParams.get("warning") || undefined,
    nonValidPartnerIndicator:
      searchParams.get("nonValidPartnerIndicator") || undefined,
    hideDeleted: searchParams.get("hideDeleted") ?? "true",
    enabled: !!representedEntity,
  });

  const itemsData =
    itemsDataFromHook || (!shouldIgnorePassedData ? passedData : undefined);

  const tradeDirection = itemsData?.reportHeader.tradeDirection;

  const { versionColumns, getTradeDirectionLabel } =
    useVersionColumns(tradeDirection);

  const businessEntityName =
    itemsData?.reportHeader?.vatPayerInfo?.vatPayerTitle;
  const businessEntityVat =
    itemsData?.reportHeader?.vatPayerInfo?.vatPayerIdentifierVAT;
  const version = itemsData?.version;
  const isEditable = version?.isEditable ?? false;
  const restricted = version?.actionsRestricted ?? false;

  // Over-limit versions show their (otherwise hidden) actions disabled, with a tooltip explaining why.
  // pointer-events-none on the disabled button lets the Tooltip wrapper still receive hover.
  const withRestrictedTooltip = (node: ReactNode) =>
    restricted ? (
      <Tooltip content={t("reportPage.tooltips.actionsRestricted")}>
        {node}
      </Tooltip>
    ) : (
      node
    );

  const versionData = useMemo(
    () =>
      version && itemsData
        ? [
            {
              ...version,
              reportingPeriod: formatReportingPeriod(
                itemsData.reportHeader.reportingPeriod.reportingYear,
                itemsData.reportHeader.reportingPeriod.reportingMonth
              ),
              tradeDirection: itemsData.reportHeader.tradeDirection,
            },
          ]
        : [],
    [version, itemsData]
  );

  const versionTable = useReactTable({
    data: versionData,
    columns: versionColumns,
    getCoreRowModel: getCoreRowModel(),
  });

  const queryClient = useQueryClient();
  const { showToast } = useToast();

  const { data: operationStatus } = useQuery({
    queryKey: ["versionOperationStatus", versionIdNum],
    queryFn: async () => {
      const response = await sdk.getReportVersionOperationStatus({
        path: { versionId: versionIdNum },
        throwOnError: true,
      });
      return response.data;
    },
    enabled: processingOperation !== null,
    refetchInterval: query =>
      query.state.data === undefined || query.state.data?.operation
        ? 2000
        : false,
  });

  useEffect(() => {
    if (processingOperation !== null) return;
    const serverOperation = version?.processingOperation;
    if (serverOperation !== "VALIDATE" && serverOperation !== "SUBMIT") return;
    const key = `${versionIdNum}:${serverOperation}`;
    if (handledOperationKeysRef.current.has(key)) return;
    queryClient.removeQueries({
      queryKey: ["versionOperationStatus", versionIdNum],
    });
    setProcessingOperation(serverOperation);
  }, [
    version?.processingOperation,
    processingOperation,
    versionIdNum,
    queryClient,
  ]);

  useEffect(() => {
    if (processingOperation === null || !operationStatus) return;
    if (operationStatus.operation) return;

    const finishedOperation = processingOperation;
    handledOperationKeysRef.current.add(`${versionIdNum}:${finishedOperation}`);

    if (operationStatus.errorMessage) {
      showToast(operationStatus.errorMessage, "error");
    } else {
      setValidationResult({
        hasErrors: operationStatus.hasErrors ?? false,
        hasWarnings: operationStatus.hasWarnings ?? false,
      });
      if (finishedOperation === "VALIDATE") {
        setShowCheckSuccess(true);
      } else {
        setShowSubmitSuccess(true);
      }
      void queryClient.invalidateQueries({ queryKey: reportKeys.all() });
    }
    setProcessingOperation(null);
  }, [
    operationStatus,
    processingOperation,
    versionIdNum,
    showToast,
    queryClient,
  ]);

  const { setBreadcrumbs } = useBreadcrumb();

  useEffect(() => {
    if (itemsData) {
      const breadcrumbs = [
        { label: t("breadcrumbs.reports"), path: "/reports/summaries" },
        {
          label: `${getTradeDirectionLabel(
            itemsData.reportHeader.tradeDirection
          )} ${formatReportingPeriod(
            itemsData.reportHeader.reportingPeriod.reportingYear,
            itemsData.reportHeader.reportingPeriod.reportingMonth
          )}`,
          path: `/report/${itemsData.reportHeader.reportId}`,
        },
        {
          label: `${t("breadcrumbs.itemList")} ${
            itemsData.version?.versionNumber ?? ""
          }`,
        },
      ];
      setBreadcrumbs(breadcrumbs);
    }
  }, [itemsData, t, setBreadcrumbs, getTradeDirectionLabel]);

  const partnerColumnWidth = useMemo(
    () =>
      estimateTextColumnWidth(
        (itemsData?.items ?? []).map(item => item.partnerIdentifier),
        { min: PARTNER_COLUMN_MIN_WIDTH, max: PARTNER_COLUMN_MAX_WIDTH }
      ),
    [itemsData?.items]
  );

  const nomenclatureColumnWidth = useMemo(
    () =>
      estimateTextColumnWidth(
        (itemsData?.items ?? []).map(item => item.combinedNomenclatureCode),
        {
          min: NOMENCLATURE_COLUMN_MIN_WIDTH,
          max: NOMENCLATURE_COLUMN_MAX_WIDTH,
        }
      ),
    [itemsData?.items]
  );

  const columns = useMemo<ColumnDef<UiReportItem>[]>(
    () =>
      [
        ...(isEditable
          ? [
              {
                id: "selection",
                header: ({ table }: HeaderContext<UiReportItem, unknown>) => {
                  const canSelectAny = table
                    .getRowModel()
                    .rows.some(row => row.getCanSelect());
                  if (!canSelectAny) return null;

                  return (
                    <div className="flex justify-center items-center">
                      <input
                        type="checkbox"
                        className="w-4 h-4 text-primary border-gray-300 rounded focus:ring-primary"
                        checked={table.getIsAllPageRowsSelected()}
                        onChange={table.getToggleAllPageRowsSelectedHandler()}
                        aria-label="Select all items"
                      />
                    </div>
                  );
                },
                cell: ({ row }: { row: Row<UiReportItem> }) => {
                  if (row.original.isDeleted) return null;
                  return (
                    <div className="flex justify-center items-center">
                      <input
                        type="checkbox"
                        className="w-4 h-4 text-primary border-gray-300 rounded focus:ring-primary"
                        checked={row.getIsSelected()}
                        onChange={row.getToggleSelectedHandler()}
                        disabled={!row.getCanSelect()}
                        aria-label={`Select item ${row.original.reportItemNumber}`}
                      />
                    </div>
                  );
                },
                size: 40,
              },
            ]
          : []),
        {
          id: "actions",
          header: "",
          size: 110,
          cell: ({ row }: { row: Row<UiReportItem> }) => {
            if (!isEditable && row.original.isDeleted) {
              return null;
            }
            return (
              <div className="flex justify-center items-center">
                <Button
                  variant={!isEditable ? "secondary" : "primary"}
                  size="md"
                  onClick={() =>
                    navigate(
                      `/reports/versions/${versionIdNum}/items/${row.original.id}`
                    )
                  }
                >
                  {!isEditable
                    ? t("reportItemsPage.buttons.open")
                    : t("reportItemsPage.buttons.edit")}
                </Button>
              </div>
            );
          },
        },
        {
          accessorKey: "reportItemNumber",
          id: "reportItemNumber",
          header: t("reportItemsPage.table.rowNumber"),
          size: 70,
          cell: (info: CellContext<UiReportItem, unknown>) => (
            <div className="flex justify-center items-center font-medium">
              {info.getValue() as number}
            </div>
          ),
        },
        {
          accessorKey: "errorIndicator",
          id: "errorIndicator",
          header: t("reportItemsPage.table.hasErrors"),
          size: 90,
          cell: (info: CellContext<UiReportItem, unknown>) => (
            <div className="flex justify-center items-center">
              {(info.getValue() as boolean) && (
                <Tooltip
                  content={t("reportItemsPage.table.hasErrors")}
                  variant="error"
                >
                  <div className="flex items-center">
                    <StatusIndicator status="error" size="lg" />
                  </div>
                </Tooltip>
              )}
            </div>
          ),
        },
        {
          accessorKey: "warningIndicator",
          id: "warningIndicator",
          header: t("reportItemsPage.table.hasWarnings"),
          size: 90,
          cell: (info: CellContext<UiReportItem, unknown>) => (
            <div className="flex justify-center items-center">
              {(info.getValue() as boolean) && (
                <Tooltip content={t("reportItemsPage.table.hasWarnings")}>
                  <div className="flex items-center">
                    <StatusIndicator status="warning" size="lg" />
                  </div>
                </Tooltip>
              )}
            </div>
          ),
        },
        {
          accessorKey: "combinedNomenclatureCode",
          id: "combinedNomenclatureCode",
          header: t("reportItemsPage.table.nomenclatureCode"),
          size: nomenclatureColumnWidth,
          cell: (info: CellContext<UiReportItem, unknown>) =>
            renderCellWithValidation(info),
        },
        {
          accessorKey: "regionCode",
          id: "regionCode",
          header: t("reportItemsPage.table.regionCode"),
          size: 90,
          cell: (info: CellContext<UiReportItem, unknown>) =>
            renderCellWithValidation(info),
        },
        {
          accessorKey: "natureOfTransactionCode",
          id: "natureOfTransactionCode",
          header: t("reportItemsPage.table.transactionCode"),
          size: 110,
          cell: (info: CellContext<UiReportItem, unknown>) =>
            renderCellWithValidation(info),
        },
        {
          accessorKey: "deliveryTermsCode",
          id: "deliveryTermsCode",
          header: t("reportItemsPage.table.deliveryTermsCode"),
          size: 120,
          cell: (info: CellContext<UiReportItem, unknown>) =>
            renderCellWithValidation(info),
        },
        {
          accessorKey: "modeOfTransportCode",
          id: "modeOfTransportCode",
          header: t("reportItemsPage.table.transportCode"),
          size: 110,
          cell: (info: CellContext<UiReportItem, unknown>) =>
            renderCellWithValidation(info),
        },
        {
          accessorKey: "countryOfDispatchOrDestinationCode",
          id: "countryOfDispatchOrDestinationCode",
          header:
            tradeDirection === "EXPORT"
              ? t("reportItemsPage.table.countryOfDestinationCode")
              : t("reportItemsPage.table.countryOfDispatchCode"),
          size: 130,
          cell: (info: CellContext<UiReportItem, unknown>) =>
            renderCellWithValidation(info),
        },
        {
          accessorKey: "countryOfOriginCode",
          id: "countryOfOriginCode",
          header: t("reportItemsPage.table.originCountryCode"),
          size: 120,
          cell: (info: CellContext<UiReportItem, unknown>) =>
            renderCellWithValidation(info),
        },
        {
          accessorKey: "partnerIdentifier",
          id: "partnerIdentifier",
          header: t("reportItemsPage.table.partnerId"),
          size: partnerColumnWidth,
          cell: (info: CellContext<UiReportItem, unknown>) =>
            renderCellWithValidation(info),
        },
        {
          accessorKey: "netMass",
          id: "netMass",
          header: t("reportItemsPage.table.netMass"),
          size: 125,
          cell: (info: CellContext<UiReportItem, unknown>) =>
            renderCellWithValidation(info),
        },
        {
          accessorKey: "unitOfMeasureCode",
          id: "unitOfMeasureCode",
          header: t("reportItemsPage.table.unitCode"),
          size: 90,
          cell: (info: CellContext<UiReportItem, unknown>) =>
            renderCellWithValidation(info),
        },
        {
          accessorKey: "quantityInSupplementaryUnitOfMeasure",
          id: "quantityInSupplementaryUnitOfMeasure",
          header: t("reportItemsPage.table.quantity"),
          size: 125,
          cell: (info: CellContext<UiReportItem, unknown>) =>
            renderCellWithValidation(info),
        },
        {
          accessorKey: "invoicedAmount",
          id: "invoicedAmount",
          header: t("reportItemsPage.table.invoicedAmount"),
          size: 140,
          cell: (info: CellContext<UiReportItem, unknown>) =>
            renderCellWithValidation(info),
        },
        {
          accessorKey: "statisticalValue",
          id: "statisticalValue",
          header: t("reportItemsPage.table.statisticalValue"),
          size: 140,
          cell: (info: CellContext<UiReportItem, unknown>) =>
            renderCellWithValidation(info),
        },
      ].filter(col => {
        if (tradeDirection !== "EXPORT") {
          return (
            col.id !== "partnerIdentifier" &&
            col.id !== "partnerVerificationResult" &&
            col.id !== "regionCode"
          );
        }
        return true;
      }),
    [
      t,
      navigate,
      versionIdNum,
      isEditable,
      tradeDirection,
      partnerColumnWidth,
      nomenclatureColumnWidth,
    ]
  );

  const table = useReactTable({
    data: itemsData?.items ?? [],
    columns,
    state: {
      rowSelection,
    },
    enableRowSelection: row => isEditable && !row.original.isDeleted,
    onRowSelectionChange: setRowSelection,
    getCoreRowModel: getCoreRowModel(),
    getRowId: (_, index) => String(index),
  });

  const isSameSearch = useCallback(
    (next: URLSearchParams) => {
      const currentParams = new URLSearchParams(searchParams);
      const nextParams = new URLSearchParams(next);

      if (currentParams.get("page") === "1") currentParams.delete("page");
      if (nextParams.get("page") === "1") nextParams.delete("page");

      currentParams.sort();
      nextParams.sort();

      return currentParams.toString() === nextParams.toString();
    },
    [searchParams]
  );

  const handlePageChange = (newPage: number) => {
    const validNewPage = Math.max(1, newPage);
    const nextParams = new URLSearchParams(searchParams);

    if (validNewPage === 1) {
      nextParams.delete("page");
    } else {
      nextParams.set("page", String(validNewPage));
    }

    setSearchParams(nextParams, {
      replace: isSameSearch(nextParams),
    });
  };

  const handleFilter = () => {
    const params: Record<string, string> = {};

    if (reportItemNumberFrom)
      params.reportItemNumberFrom = reportItemNumberFrom;
    if (reportItemNumberTo) params.reportItemNumberTo = reportItemNumberTo;
    if (combinedNomenclatureCode)
      params.combinedNomenclatureCode = combinedNomenclatureCode;
    if (partnerIdentifier) params.partnerIdentifier = partnerIdentifier;

    if (errorFilter === "true") params.error = "true";
    if (warningFilter === "true") params.warning = "true";

    if (nonValidPartnerIndicatorFilter)
      params.nonValidPartnerIndicator = nonValidPartnerIndicatorFilter;
    if (hideDeletedFilter) params.hideDeleted = hideDeletedFilter;

    const nextSearchParams = new URLSearchParams(params);
    setSearchParams(params, {
      replace: isSameSearch(nextSearchParams),
    });
  };

  const handleClearFilter = () => {
    setReportItemNumberFrom("");
    setReportItemNumberTo("");
    setCombinedNomenclatureCode("");
    setPartnerIdentifier("");
    setErrorFilter("");
    setWarningFilter("");
    setNonValidPartnerIndicatorFilter("");
    setHideDeletedFilter("true");

    setIsFiltersOpen(true);

    setSearchParams(
      { hideDeleted: "true" },
      { replace: isSameSearch(new URLSearchParams({ hideDeleted: "true" })) }
    );
  };

  const { data: headerData } = useReportVersionHeader(
    versionIdNum,
    !!representedEntity
  );
  const { mutate: updateZeroTrade } =
    useUpdateReportVersionZeroTrade(versionIdNum);

  const [zeroTradeLocal, setZeroTradeLocal] = useState<boolean>(false);
  const showItemsSection = isEditable || !zeroTradeLocal;
  const [isHeaderEditable, setIsHeaderEditable] = useState<boolean>(false);
  const [isHeaderAccordionOpen, setIsHeaderAccordionOpen] =
    useState<boolean>(false);
  const hasAutoExpandedRef = useRef<boolean>(false);

  useEffect(() => {
    if (headerData && !hasAutoExpandedRef.current) {
      const hasHeaderErrors = headerData.validationErrors?.some(error =>
        error.validationRuleDataFields?.some(field =>
          VALID_HEADER_FIELDS.includes(field.dataFieldName || "")
        )
      );

      if (hasHeaderErrors) {
        setIsHeaderAccordionOpen(true);
      }
      hasAutoExpandedRef.current = true;
    }
  }, [headerData]);

  useEffect(() => {
    if (headerData?.version?.zeroTradeIndicator !== undefined) {
      setZeroTradeLocal(headerData.version.zeroTradeIndicator);
    }
  }, [headerData?.version?.zeroTradeIndicator]);

  const handleZeroTradeChange = (checked: boolean) => {
    setZeroTradeLocal(checked);
    updateZeroTrade(checked, {
      onError: (error: unknown) => {
        showToast(handleApiError(error as Error, t), "error");
        setZeroTradeLocal(headerData?.version?.zeroTradeIndicator ?? false);
      },
    });
  };

  const executeValidateReport = async () => {
    setShowCheckConfirm(false);
    try {
      await sdk.validateReportVersion<true>({
        throwOnError: true,
        path: {
          versionId: versionIdNum,
        },
      });
      queryClient.removeQueries({
        queryKey: ["versionOperationStatus", versionIdNum],
      });
      setProcessingOperation("VALIDATE");
    } catch (error) {
      showToast(
        t("reportItemsPage.notifications.reportValidationFailed"),
        "error"
      );
      console.error("Validation failed:", error);
    }
  };

  const handleValidateReport = () => {
    setShowCheckConfirm(true);
  };

  const executeSubmitReport = async () => {
    setShowSubmitConfirm(false);
    try {
      await sdk.submitReportVersion<true>({
        throwOnError: true,
        path: {
          versionId: versionIdNum,
        },
      });
      queryClient.removeQueries({
        queryKey: ["versionOperationStatus", versionIdNum],
      });
      setProcessingOperation("SUBMIT");
    } catch (error) {
      showToast(
        t("reportItemsPage.notifications.reportSubmissionFailed"),
        "error"
      );
      console.error("Submission failed:", error);
    }
  };

  const handleSubmitReport = () => {
    setShowSubmitConfirm(true);
  };

  const executeBulkDelete = () => {
    setShowBulkDeleteConfirm(false);
    const selectedIds = Object.keys(rowSelection)
      .filter(key => rowSelection[key])
      .map(key => itemsData?.items[Number(key)]?.id)
      .filter((id): id is number => id !== undefined);

    if (selectedIds.length === 0) return;

    deleteItems(selectedIds, {
      onSuccess: () => {
        showToast(
          t("reportItemsPage.notifications.deletedSuccessfully"),
          "success"
        );
        setRowSelection({});
      },
      onError: () => {
        showToast(t("reportItemsPage.notifications.deleteFailed"), "error");
      },
    });
  };

  const handleBulkDelete = () => {
    const selectedIds = Object.keys(rowSelection)
      .filter(key => rowSelection[key])
      .map(key => itemsData?.items[Number(key)]?.id)
      .filter((id): id is number => id !== undefined);

    if (selectedIds.length === 0) return;
    setShowBulkDeleteConfirm(true);
  };

  const handleCopyItem = () => {
    const selectedIndices = Object.keys(rowSelection).filter(
      key => rowSelection[key]
    );
    if (selectedIndices.length !== 1) return;

    const itemId = itemsData?.items[Number(selectedIndices[0])]?.id;
    if (!itemId) return;

    copyItem(itemId, {
      onSuccess: () => {
        showToast(
          t("reportItemsPage.notifications.copiedSuccessfully"),
          "success"
        );
        setRowSelection({});
      },
      onError: () => {
        showToast(t("reportItemsPage.notifications.copyFailed"), "error");
      },
    });
  };

  const renderRadioGroup = (
    label: string,
    value: string,
    onChange: (val: string) => void,
    options: { label: string; value: string }[]
  ) => (
    <div>
      <label className="block text-sm font-medium text-black mb-2">
        {label}
      </label>
      <div className="flex flex-col space-y-2">
        {options.map(option => (
          <label
            key={option.value}
            className="flex items-center space-x-2 cursor-pointer"
          >
            <input
              type="radio"
              className="w-4 h-4 text-primary border-gray-300 focus:ring-primary"
              checked={value === option.value}
              onChange={() => onChange(option.value)}
            />
            <span className="text-sm text-gray-700">{option.label}</span>
          </label>
        ))}
      </div>
    </div>
  );

  if (!representedEntity) {
    return <Loader />;
  }

  if (loading && !itemsData) {
    return <Loader />;
  }

  if (isError) {
    return <ErrorDisplay message={handleApiError(error, t)} />;
  }

  if (!itemsData) {
    return <NoData />;
  }

  const selectedCount = Object.keys(rowSelection).filter(
    key => rowSelection[key]
  ).length;

  return (
    <div className="space-y-8">
      <PageTitle>{t("reportItemsPage.versionDetails")}</PageTitle>

      <div className="mt-4">
        <div className="space-y-1 text-sm text-black mb-6">
          <div className="whitespace-nowrap">
            <span className="font-bold">
              {t("reportPage.reportInfo.vatPayerTitle")}:
            </span>{" "}
            {businessEntityName}
          </div>
          <div className="whitespace-nowrap">
            <span className="font-bold">
              {t("reportPage.reportInfo.vatPayerCode")}:
            </span>{" "}
            {businessEntityVat}
          </div>
          <div className="pt-2">
            <label className="flex items-center space-x-2 cursor-pointer select-none">
              <input
                type="checkbox"
                className="w-4 h-4 text-primary border-gray-300 rounded focus:ring-primary disabled:opacity-50 disabled:cursor-not-allowed"
                checked={zeroTradeLocal}
                disabled={
                  !isEditable || (isHeaderEditable && isHeaderAccordionOpen)
                }
                onChange={e => handleZeroTradeChange(e.target.checked)}
              />
              <span
                className={`font-bold ${
                  !isEditable || (isHeaderEditable && isHeaderAccordionOpen)
                    ? "text-gray-400"
                    : ""
                }`}
              >
                {t("reportVersionHeaderPage.zeroTradeIndicator")}
              </span>
              {isEditable && isHeaderEditable && isHeaderAccordionOpen && (
                <span className="text-xs text-gray-500 italic ml-2">
                  (
                  {t("reportItemsPage.saveHeaderToModify") ||
                    "Save header to modify"}
                  )
                </span>
              )}
            </label>
          </div>
        </div>
        <div className="bg-white shadow rounded-lg overflow-hidden">
          <HeadlessTable table={versionTable} />
        </div>
      </div>

      <div className="flex space-x-3">
        <Button variant="secondary" size="md" className="hidden!">
          {t("reportItemsPage.buttons.saveReport")}
        </Button>
        {withRestrictedTooltip(
          <Button
            variant="secondary"
            size="md"
            onClick={handleValidateReport}
            loading={isValidatingReport}
            loadingText={t("reportItemsPage.buttons.checkingReport")}
            disabled={restricted}
            className={
              !isEditable ? "hidden!" : restricted ? "pointer-events-none" : ""
            }
          >
            {t("reportItemsPage.buttons.checkReport")}
          </Button>
        )}
        {withRestrictedTooltip(
          <Button
            variant="primary"
            size="md"
            onClick={handleSubmitReport}
            loading={isSubmittingReport}
            loadingText={t("reportItemsPage.buttons.submittingReport")}
            disabled={restricted}
            className={
              !isEditable ? "hidden!" : restricted ? "pointer-events-none" : ""
            }
          >
            {t("reportItemsPage.buttons.submitReport")}
          </Button>
        )}
        {(version?.isError || version?.isWarning) && (
          <Button
            variant="error"
            size="md"
            onClick={() => navigate(`/reports/versions/${versionIdNum}/errors`)}
          >
            {t("reportItemsPage.buttons.downloadErrorsWarnings")}
          </Button>
        )}
      </div>

      <Accordion
        title={t("reportPage.table.header")}
        isOpen={isHeaderAccordionOpen}
        onToggle={setIsHeaderAccordionOpen}
      >
        <ReportVersionHeader
          versionId={versionIdNum}
          onEditingChange={setIsHeaderEditable}
          onSaveSuccess={() => setIsHeaderAccordionOpen(false)}
        />
      </Accordion>

      {showItemsSection && (
        <Accordion
          title={t("reportItemsPage.items")}
          isOpen={isFiltersOpen}
          onToggle={setIsFiltersOpen}
        >
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6 pt-4">
            <FormInput
              label={t("reportItemsPage.filters.itemNumberFrom")}
              value={reportItemNumberFrom}
              onChange={val => {
                if (!val || Number(val) >= 0) setReportItemNumberFrom(val);
              }}
              type="number"
              min="0"
            />
            <FormInput
              label={t("reportItemsPage.filters.itemNumberTo")}
              value={reportItemNumberTo}
              onChange={val => {
                if (!val || Number(val) >= 0) setReportItemNumberTo(val);
              }}
              type="number"
              min="0"
            />
            <FormInput
              label={t("reportItemsPage.table.nomenclatureCode")}
              value={combinedNomenclatureCode}
              onChange={setCombinedNomenclatureCode}
            />
            {tradeDirection === "EXPORT" && (
              <FormInput
                label={t("reportItemsPage.table.partnerId")}
                value={partnerIdentifier}
                onChange={setPartnerIdentifier}
              />
            )}
          </div>

          <div className="border-b border-gray-200 mb-6" />

          <div className="flex flex-col md:flex-row md:space-x-20 space-y-4 md:space-y-0 mb-6 px-10">
            <div className="flex flex-col space-y-4 justify-center">
              <label className="flex items-center space-x-2 cursor-pointer select-none">
                <input
                  type="checkbox"
                  className="w-4 h-4 text-primary border-gray-300 rounded focus:ring-primary"
                  checked={errorFilter === "true"}
                  onChange={e => setErrorFilter(e.target.checked ? "true" : "")}
                />
                <span className="text-sm font-medium text-gray-700">
                  {t("reportItemsPage.filters.hasErrors")}
                </span>
              </label>
              <label className="flex items-center space-x-2 cursor-pointer select-none">
                <input
                  type="checkbox"
                  className="w-4 h-4 text-primary border-gray-300 rounded focus:ring-primary"
                  checked={warningFilter === "true"}
                  onChange={e =>
                    setWarningFilter(e.target.checked ? "true" : "")
                  }
                />
                <span className="text-sm font-medium text-gray-700">
                  {t("reportItemsPage.filters.hasWarnings")}
                </span>
              </label>
            </div>
            {tradeDirection === "EXPORT" &&
              renderRadioGroup(
                t("reportVersionHeaderPage.nonValidPartnerIndicator"),
                nonValidPartnerIndicatorFilter,
                setNonValidPartnerIndicatorFilter,
                [
                  {
                    label: t("reportItemsPage.filters.all"),
                    value: "",
                  },
                  {
                    label: t("common.yes"),
                    value: "true",
                  },
                  {
                    label: t("common.no"),
                    value: "false",
                  },
                ]
              )}
          </div>

          <div className="border-b border-gray-200 mb-6" />

          <div className="flex justify-start space-x-3">
            <Button
              variant="primary"
              size="md"
              onClick={handleFilter}
              className="mb-0.5"
            >
              {t("reportItemsPage.filters.filter")}
            </Button>
            <Button
              variant="secondary"
              size="md"
              onClick={handleClearFilter}
              className="mb-0.5"
            >
              {t("common.clearFilter")}
            </Button>
          </div>
        </Accordion>
      )}

      <div className="space-y-8">
        <div className="flex flex-col items-start space-y-4">
          <span className="text-gray-900 font-semibold text-lg text-left">
            {t("reportItemsPage.goods")}
          </span>
          <div className="flex space-x-3">
            <Button
              variant="primary"
              size="md"
              onClick={() =>
                navigate(`/reports/versions/${versionIdNum}/items/new`)
              }
              className={!isEditable ? "hidden!" : ""}
            >
              {t("reportItemsPage.buttons.addNewItem")}
            </Button>

            {isEditable && selectedCount === 1 && (
              <Button
                variant="secondary"
                size="md"
                onClick={handleCopyItem}
                loading={isCopying}
              >
                {t("reportItemsPage.buttons.copyItem")}
              </Button>
            )}

            {isEditable && selectedCount > 0 && (
              <Button
                variant="error"
                size="md"
                onClick={handleBulkDelete}
                loading={isDeleting}
              >
                {t("reportItemsPage.buttons.deleteItems")}
              </Button>
            )}
          </div>

          {showItemsSection && itemsData?.hasDeletedItems && (
            <label className="flex items-center space-x-2 cursor-pointer select-none">
              <input
                type="checkbox"
                className="w-4 h-4 text-primary border-gray-300 rounded focus:ring-primary"
                checked={hideDeletedFilter === "true"}
                onChange={e => {
                  const newValue = e.target.checked ? "true" : "false";
                  setHideDeletedFilter(newValue);
                  const nextParams = new URLSearchParams(searchParams);
                  if (newValue === "true") {
                    nextParams.set("hideDeleted", "true");
                  } else {
                    nextParams.set("hideDeleted", "false");
                  }
                  nextParams.delete("page");
                  setSearchParams(nextParams, { replace: true });
                }}
              />
              <span className="text-sm font-medium text-gray-700">
                {t("reportItemsPage.filters.hideDeleted")}
              </span>
            </label>
          )}
        </div>
      </div>

      <HeadlessTable
        table={table}
        pagination={itemsData.pagination}
        onPageChange={handlePageChange}
        rowClassName={(row: UiReportItem) =>
          row.isDeleted ? "bg-gray-100!" : ""
        }
        emptyMessage={t("reportItemsPage.table.reportHasNoItems")}
        renderRowOverlay={(row: UiReportItem) =>
          row.isDeleted ? (
            <div
              className="absolute inset-y-0 right-0 flex items-center justify-center bg-gray-100/60 z-10"
              style={{ left: "180px" }}
            >
              <span className="text-gray-500 font-bold uppercase tracking-wider text-xs">
                {t("reportItemsPage.itemDeleted") || "Item deleted"}
              </span>
            </div>
          ) : null
        }
      />

      {showCheckConfirm && (
        <Modal
          show={showCheckConfirm}
          onHide={() => setShowCheckConfirm(false)}
          closable={false}
          footer={
            <div className="flex justify-center gap-4 w-full">
              <Button
                variant="secondary"
                size="xl"
                onClick={() => setShowCheckConfirm(false)}
              >
                {t("common.no")}
              </Button>
              <Button
                variant="primary"
                size="xl"
                onClick={executeValidateReport}
              >
                {t("common.yes")}
              </Button>
            </div>
          }
        >
          <div className="py-4 text-xl font-bold text-center text-gray-900">
            {t("reportItemsPage.confirmCheck")}
          </div>
        </Modal>
      )}

      {showSubmitConfirm && (
        <Modal
          show={showSubmitConfirm}
          onHide={() => setShowSubmitConfirm(false)}
          closable={false}
          footer={
            <div className="flex justify-center gap-4 w-full">
              <Button
                variant="secondary"
                size="xl"
                onClick={() => setShowSubmitConfirm(false)}
              >
                {t("common.no")}
              </Button>
              <Button variant="primary" size="xl" onClick={executeSubmitReport}>
                {t("common.yes")}
              </Button>
            </div>
          }
        >
          <div className="py-4 text-xl font-bold text-center text-gray-900">
            {t("reportItemsPage.confirmSubmit")}
          </div>
        </Modal>
      )}

      {showCheckSuccess && (
        <Modal
          show={showCheckSuccess}
          onHide={() => setShowCheckSuccess(false)}
          closable={false}
          footer={
            <div className="flex justify-center w-full">
              <Button
                variant="primary"
                size="xl"
                onClick={() => setShowCheckSuccess(false)}
              >
                {t("common.ok")}
              </Button>
            </div>
          }
        >
          <div className="py-4 text-center">
            {validationResult?.hasErrors ? (
              <div className="text-xl font-bold text-gray-900">
                {t("reportItemsPage.checkSuccessWithErrors") ||
                  "Report checked successfully, but errors were found."}
              </div>
            ) : (
              <div className="text-xl font-bold text-gray-900">
                {t("reportItemsPage.notifications.reportValidatedSuccessfully")}
              </div>
            )}
          </div>
        </Modal>
      )}

      {showSubmitSuccess && (
        <Modal
          show={showSubmitSuccess}
          onHide={() => {
            setShowSubmitSuccess(false);
            if (!validationResult?.hasErrors) {
              navigate(`/report/${itemsData?.reportHeader.reportId}`);
            }
          }}
          closable={false}
          footer={
            <div className="flex justify-center w-full">
              <Button
                variant="primary"
                size="xl"
                onClick={() => {
                  setShowSubmitSuccess(false);
                  if (!validationResult?.hasErrors) {
                    navigate(`/report/${itemsData?.reportHeader.reportId}`);
                  }
                }}
              >
                {t("common.ok")}
              </Button>
            </div>
          }
        >
          <div className="py-4 text-center">
            {validationResult?.hasErrors ? (
              <div className="text-xl font-bold text-gray-900">
                {t("reportItemsPage.submitSuccessWithErrors") ||
                  "Report submitted successfully, but it contains errors."}
              </div>
            ) : (
              <div className="text-xl font-bold text-gray-900">
                {t("reportItemsPage.notifications.reportSubmittedSuccessfully")}
              </div>
            )}
          </div>
        </Modal>
      )}

      {showBulkDeleteConfirm && (
        <Modal
          show={showBulkDeleteConfirm}
          onHide={() => setShowBulkDeleteConfirm(false)}
          closable={false}
          footer={
            <div className="flex justify-center gap-4 w-full">
              <Button
                variant="secondary"
                size="xl"
                onClick={() => setShowBulkDeleteConfirm(false)}
              >
                {t("common.no")}
              </Button>
              <Button variant="error" size="xl" onClick={executeBulkDelete}>
                {t("common.yes")}
              </Button>
            </div>
          }
        >
          <div className="py-4 text-xl font-bold text-center text-gray-900">
            {t("reportItemsPage.confirmBulkDelete")}
          </div>
        </Modal>
      )}
    </div>
  );
}
